import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import CampoFoto from '../../components/CampoFoto';
import { compressImage } from '../../lib/compressImage';
import {
  listPuntosDeVenta, listVisitasDeRuta, misTiendasACargo,
  registrarVisitaDeRuta, subirFotoDeVisita, type VisitaEnLista,
} from '../../api/ruta';
import {
  INSTRUMENTOS, canalesOfrecidos, coberturaDeLaSemana, esRegistroTardio, loQueFaltaEnVisita,
  rotuloDelPunto, type Instrumento, type TiendaElegible, type VisitaDeRuta,
} from '../../domain/ruta';
import { getGpsFix, gpsErrorMessage, type GpsErrorKind, type GpsFix } from './Marcar';

const TZ = 'America/Lima';
const hoyLima = (): string => formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');

/** Valor del `<select>` para el camino de escribir el nombre a mano. */
const OTRA = 'otra';

/**
 * Identidad de una opción del desplegable.
 *
 * Antes el valor del `<select>` era el ÍNDICE en la lista, porque las tiendas del estudio de canal
 * moderno venían sin `pdvId`. Desde la 0096 todo lo que se ofrece sale de la maestra `pdv` y trae
 * su id, así que el id sirve de identidad — y hace falta que sirva: la lista se vuelve a pedir cada
 * vez que cambian los formularios marcados, y con índices el mismo número apuntaría a otro punto de
 * venta después de recargar.
 */
const claveDePunto = (t: TiendaElegible): string => t.pdvId ?? t.nombre;

/**
 * Rótulos femeninos. Es la excepción y por eso se listan: cualquier rótulo nuevo cae en masculino,
 * que es lo que corresponde a «punto de venta».
 */
const PUNTOS_FEMENINOS = new Set(['tienda', 'bodega']);

/**
 * Las formas del rótulo del lugar que hacen falta en la pantalla.
 *
 * `rotuloDelPunto` devuelve el sustantivo solo —«tienda» o «punto de venta»— y en español el
 * artículo va con el género: «la tienda», pero «el punto de venta». Sin esto salen los «el tienda»
 * y las «Otra punto de venta», que es la misma clase de descuido que el usuario ya reportó cuando
 * la pregunta le decía «tienda» a una bodega de esquina.
 */
function formasDelPunto(punto: string): { el: string; del: string; otro: string } {
  const fem = PUNTOS_FEMENINOS.has(punto);
  return {
    el: `${fem ? 'la' : 'el'} ${punto}`,
    del: `${fem ? 'de la' : 'del'} ${punto}`,
    otro: `${fem ? 'Otra' : 'Otro'} ${punto}`,
  };
}

/**
 * Por qué la lista trae lo que trae, dicho antes de que el supervisor se pregunte por qué falta lo
 * suyo.
 *
 * Sale de la misma corrección que el filtro por canal —*«cuando es bodegas… no debería salir Makro
 * ni Plaza Vea ni Tottus»*—: ahora que la lista deja cosas afuera, hay que decir cuáles, o el que
 * no encuentre su bodega va a creer que no está cargada en el sistema. Con los dos formularios
 * marcados la lista vuelve a traer de todo, y decir «es la del canal tradicional» ahí sería
 * mentira: por eso son tres frases y no una.
 */
function ayudaDeLaLista(instrumentos: Instrumento[]): string | null {
  const gondola = instrumentos.includes('pg_gondola');
  const bodega = instrumentos.includes('bodega');
  if (gondola && bodega) {
    return 'Marcaste los dos formularios, así que la lista trae supermercados y también bodegas y '
      + 'mercados.';
  }
  if (bodega) {
    return 'Puede ser un mercado o una bodega de calle: solo salen los del canal tradicional.';
  }
  if (gondola) return 'Solo salen supermercados y mayoristas, que es donde se levanta la góndola.';
  // Visita a secas: la lista es la maestra entera y no hay nada que explicar.
  return null;
}

/** Lo que queda en pantalla después de registrar, para poder ofrecer el formulario que sigue. */
interface VisitaRegistrada {
  mensaje: string;
  /** Viaja como `?visita=` al formulario. Ver el comentario del cartel verde, más abajo. */
  id: string;
  instrumentos: Instrumento[];
}

/**
 * El lunes de la semana de `fecha` (YYYY-MM-DD), como cadena.
 *
 * Mediodía UTC y aritmética sobre los números de la fecha, igual que `domain/positions.ts`:
 * `new Date('YYYY-MM-DD')` es medianoche UTC, que en Lima (UTC-5) cae el día anterior y correría
 * la semana entera un día para atrás. Acá eso no sería un detalle: el lunes mal calculado deja
 * fuera del conteo las visitas del propio lunes, que es el día de más recorrido.
 */
function lunesDeLaSemana(fecha: string): string {
  const [y, m, d] = fecha.split('-').map(Number);
  const diaSemana = new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).getUTCDay(); // 0 = domingo
  const lunes = new Date(Date.UTC(y, m - 1, d - (diaSemana === 0 ? 6 : diaSemana - 1), 12, 0, 0));
  const mm = String(lunes.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(lunes.getUTCDate()).padStart(2, '0');
  return `${lunes.getUTCFullYear()}-${mm}-${dd}`;
}

/** Fecha sola (YYYY-MM-DD) escrita como se lee. Mediodía UTC por lo mismo de arriba. */
const fechaLarga = (fecha: string): string =>
  formatInTimeZone(new Date(`${fecha}T12:00:00Z`), TZ, 'dd/MM/yyyy');

const etiquetaInstrumento = (valor: string): string =>
  INSTRUMENTOS.find((i) => i.valor === valor)?.etiqueta ?? valor;

function Grupo({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="mb-3 text-base font-bold text-gray-900">{titulo}</h2>
      {children}
    </section>
  );
}

/** Una bandera de la fila ya guardada. Gris: informa, no acusa. */
function Bandera({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{children}</span>
  );
}

/**
 * MI RUTA — la pantalla del supervisor de campo.
 *
 * No hay marcado de asistencia acá y no se nombra: el supervisor no marca ingreso ni salida
 * (decisión del usuario del 5-sep), y su unidad de trabajo es la VISITA. El formulario está
 * siempre abierto: no depende de turno, de asignación ni de campaña.
 *
 * Tres cosas que explican la forma de esta pantalla:
 *
 * 1. **Se puede registrar después.** La fecha se elige y admite días pasados; la pantalla AVISA que
 *    va a quedar como registro tardío y no lo impide. La bandera la pone el servidor.
 * 2. **Nada bloquea el registro salvo la tienda.** Ni la foto, ni el GPS, ni que la lista de
 *    tiendas se haya podido leer. Es la lección de las 46 fichas (BITACORA §29): un formulario que
 *    se niega a guardar produce cero datos, no datos mejores.
 * 3. **Se registran varias seguidas.** Al terminar una visita el formulario se limpia y se queda
 *    acá, con la lista del día actualizada, en vez de irse a una pantalla de confirmación: en un
 *    recorrido son ocho o diez tiendas, no una.
 */
export default function MiRuta() {
  // `hoy` se congela al abrir la pantalla. Es la referencia del aviso de registro tardío y del
  // rango de la semana; que cambiara sola a medianoche movería el aviso debajo de lo que la
  // persona ya eligió, en el peor momento posible (el supervisor que carga su ruta de noche).
  const [hoy] = useState(hoyLima);

  const [fecha, setFecha] = useState(hoy);
  // Encendida por defecto: es lo normal de una ruta. *«El supervisor siempre es parte de su visita
  // el ir a visitar a la impulsadora; las otras acciones son adicionales a la visita»*.
  const [visitoImpulsadora, setVisitoImpulsadora] = useState(true);
  const [instrumentos, setInstrumentos] = useState<Instrumento[]>([]);
  const [tiendaSel, setTiendaSel] = useState('');
  const [otraTienda, setOtraTienda] = useState('');
  const [notas, setNotas] = useState('');
  const [foto, setFoto] = useState<Blob | null>(null);
  const [procesandoFoto, setProcesandoFoto] = useState(false);

  const [puntos, setPuntos] = useState<TiendaElegible[]>([]);
  const [cargandoTiendas, setCargandoTiendas] = useState(true);
  // Se distingue «no se pudo leer» de «el canal no tiene puntos» porque las dos terminan con la
  // lista vacía y el camino a seguir es el mismo (escribir el nombre), pero decirle «no se pudo
  // cargar» a alguien que tiene señal lo manda a pelear con su celular por un problema que no
  // existe. Antes del filtro por canal la lista vacía solo podía ser una falla; ahora no.
  const [errorPuntos, setErrorPuntos] = useState(false);

  const [gps, setGps] = useState<GpsFix | null>(null);
  const [gpsListo, setGpsListo] = useState(false);
  const [gpsError, setGpsError] = useState<GpsErrorKind | null>(null);

  const [visitasSemana, setVisitasSemana] = useState<VisitaEnLista[]>([]);
  const [aCargo, setACargo] = useState<{ id: string; nombre: string }[]>([]);
  const [cargandoVisitas, setCargandoVisitas] = useState(true);
  const [errorVisitas, setErrorVisitas] = useState('');

  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [ofrecerSinFoto, setOfrecerSinFoto] = useState(false);
  const [exito, setExito] = useState<VisitaRegistrada | null>(null);

  /**
   * El `client_uuid` de la visita que se está registrando AHORA.
   *
   * Vive en un ref y no se regenera al reintentar: es lo único que vuelve idempotente el segundo
   * intento. Si la foto ya subió y falló la RPC, reintentar con el mismo uuid reescribe el mismo
   * objeto y devuelve la misma fila, en vez de dejar una foto huérfana y una visita duplicada.
   * Se limpia recién cuando la visita quedó registrada y empieza otra.
   */
  const uuidRef = useRef<string | null>(null);

  /**
   * La lista de puntos de venta, PEDIDA SEGÚN LO QUE SE VA A LEVANTAR.
   *
   * Se vuelve a pedir cada vez que cambian los formularios marcados porque el canal es lo que
   * decide qué puntos tiene sentido ofrecer: *«cuando es bodegas… no debería salir Makro ni Plaza
   * Vea ni Tottus»*. Ya no hay lista aparte de supermercados: desde la 0096 la maestra distingue
   * `canal` y `listPuntosDeVenta` filtra por ahí, así que una sola lectura alcanza.
   */
  useEffect(() => {
    let vivo = true;
    setCargandoTiendas(true);
    void (async () => {
      try {
        const lista = await listPuntosDeVenta(canalesOfrecidos(instrumentos));
        if (!vivo) return;
        setPuntos(lista);
        setErrorPuntos(false);
        // Si lo ya elegido no está entre los puntos del canal nuevo, se limpia. Sin esto quedaría
        // seleccionado un Makro dentro de una ficha de bodegas: la fila se guardaría con un punto
        // de venta que no corresponde al formulario, y eso no se nota después.
        setTiendaSel((sel) =>
          sel === OTRA || lista.some((t) => claveDePunto(t) === sel) ? sel : '');
      } catch {
        // A propósito sin mensaje de error arriba: no poder leer la lista no es un problema del
        // supervisor y no hay nada que él tenga que arreglar. La pantalla se lo dice donde le
        // sirve —en el campo del punto de venta— y le deja escribir el nombre.
        if (vivo) { setPuntos([]); setErrorPuntos(true); }
      } finally {
        if (vivo) setCargandoTiendas(false);
      }
    })();
    return () => { vivo = false; };
  }, [instrumentos]);

  // GPS en segundo plano, una sola vez. No se reintenta solo: en iOS el aviso de permiso solo
  // aparece de forma fiable desde un toque, así que el reintento es un botón.
  const pedirUbicacion = useCallback(async () => {
    setGpsListo(false);
    const { fix, error: motivo } = await getGpsFix();
    setGps(fix);
    setGpsError(motivo);
    setGpsListo(true);
  }, []);

  useEffect(() => {
    let vivo = true;
    void getGpsFix().then(({ fix, error: motivo }) => {
      if (!vivo) return;
      setGps(fix);
      setGpsError(motivo);
      setGpsListo(true);
    });
    return () => { vivo = false; };
  }, []);

  /**
   * Una sola lectura para las dos cosas de abajo: las visitas de HOY salen de filtrar las de la
   * semana. Pedir el día y la semana por separado serían dos viajes solapados en un celular que
   * muchas veces está en 3G dentro de una tienda.
   */
  const recargar = useCallback(async () => {
    setCargandoVisitas(true);
    try {
      const [semana, mias] = await Promise.all([
        listVisitasDeRuta(lunesDeLaSemana(hoy), hoy),
        misTiendasACargo(),
      ]);
      setVisitasSemana(semana);
      setACargo(mias);
      setErrorVisitas('');
    } catch (e) {
      setErrorVisitas(e instanceof Error ? e.message : 'No se pudieron cargar tus visitas.');
    } finally {
      setCargandoVisitas(false);
    }
  }, [hoy]);

  useEffect(() => { void recargar(); }, [recargar]);

  const elegida: TiendaElegible | null =
    tiendaSel === '' || tiendaSel === OTRA
      ? null
      : puntos.find((t) => claveDePunto(t) === tiendaSel) ?? null;

  const escribiendoTienda = tiendaSel === OTRA || (!cargandoTiendas && puntos.length === 0);
  const tardio = esRegistroTardio(fecha, hoy);

  // Cómo se llama el lugar en ESTE formulario. Una bodega de esquina no es una «tienda», y el
  // usuario lo pidió por su nombre: se usa en la pregunta, en la opción de escribirlo a mano y en
  // los textos de ayuda que lo nombran.
  const punto = rotuloDelPunto(instrumentos);
  const formas = formasDelPunto(punto);
  const ayudaLista = ayudaDeLaLista(instrumentos);

  /**
   * Lo que se manda. `fotoPath` entra por parámetro porque recién se conoce al subir la foto, y
   * esta misma función arma la visita "en borrador" con la que se decide si el botón se habilita.
   */
  function armarVisita(fotoPath: string | null): VisitaDeRuta {
    return {
      fecha,
      // El momento en que estuvo en la tienda. Solo se declara cuando la visita es de HOY: si la
      // está cargando de memoria de un día pasado, la hora en que llenó el formulario no es la
      // hora en que estuvo ahí, y mandarla sería inventar un dato preciso.
      ocurrioEn: fecha === hoy ? new Date().toISOString() : '',
      pdvId: escribiendoTienda ? null : elegida?.pdvId ?? null,
      // Todo lo que se ofrece sale de la maestra, así que el texto libre es el camino de escribir
      // el nombre a mano. La otra rama queda por si una opción llegara sin `pdvId`: mandar el
      // nombre es lo único que permitiría saber después dónde estuvo. El servidor la marca como
      // `tienda_fuera_de_maestra`, que es exactamente lo que es.
      tiendaTexto: escribiendoTienda
        ? otraTienda.trim()
        : elegida !== null && elegida.pdvId === null ? elegida.nombre : '',
      // Los tres van en null en esta primera versión: la visita puede no colgar de ninguna
      // campaña ni estudio —el levantamiento por iniciativa propia es el caso que lo obligó— y de
      // la impulsadora se habla más abajo.
      campaignId: null,
      estudioId: null,
      impulsadoraId: null,
      visitoImpulsadora,
      instrumentos,
      notas: notas.trim(),
      lat: gps?.lat ?? null,
      lng: gps?.lng ?? null,
      accuracyM: gps?.accuracy ?? null,
      fotoPath,
    };
  }

  const falta = loQueFaltaEnVisita(armarVisita(null));

  /**
   * Marca o desmarca un formulario. Los dos pueden estar marcados a la vez: son ADICIONALES a la
   * visita y entre ellos tampoco se excluyen —una parada puede levantar góndola y bodega—.
   *
   * No limpia el punto de venta elegido: de eso se encarga la recarga de la lista, y solo cuando
   * el punto de verdad dejó de estar entre los del canal nuevo. Limpiarlo acá siempre obligaría a
   * volver a elegir la misma tienda por haber marcado una casilla.
   */
  function alternarInstrumento(valor: Instrumento): void {
    setInstrumentos((prev) =>
      prev.includes(valor) ? prev.filter((i) => i !== valor) : [...prev, valor]);
    setError('');
    setExito(null);
  }

  async function elegirFoto(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setProcesandoFoto(true);
    // Se comprime al elegirla y no al subirla: una foto de 4 MB por 3G dentro de una tienda es la
    // diferencia entre subir y no subir.
    setFoto(await compressImage(file));
    setProcesandoFoto(false);
  }

  function limpiarFormulario(): void {
    // Se conservan la FECHA y lo marcado arriba —la visita a la impulsadora y los formularios—: el
    // caso normal es registrar varias paradas seguidas del mismo recorrido, y volver a elegir lo
    // mismo en cada punto de venta es teclear ocho veces lo que no cambió.
    setTiendaSel('');
    setOtraTienda('');
    setNotas('');
    setFoto(null);
    uuidRef.current = null;
  }

  async function enviar(sinFoto = false): Promise<void> {
    const pendiente = loQueFaltaEnVisita(armarVisita(null));
    if (pendiente !== null) { setError(pendiente); return; }
    setEnviando(true);
    setError('');
    setOfrecerSinFoto(false);
    try {
      if (uuidRef.current === null) uuidRef.current = crypto.randomUUID();
      const clientUuid = uuidRef.current;

      let fotoPath: string | null = null;
      if (foto !== null && !sinFoto) {
        try {
          fotoPath = await subirFotoDeVisita(clientUuid, foto);
        } catch (e) {
          // La foto que no sube NO se lleva puesta la visita: se ofrece registrarla sin ella. El
          // dato de que estuvo en esa tienda ese día no se puede reconstruir después; la foto sí
          // se puede volver a pedir.
          setOfrecerSinFoto(true);
          setError(e instanceof Error ? e.message : 'No se pudo subir la foto.');
          return;
        }
      }

      const visita = armarVisita(fotoPath);
      const visitaId = await registrarVisitaDeRuta(visita, clientUuid);
      // La confirmación NOMBRA el punto de venta: se registran varias seguidas y un «listo» a
      // secas deja la duda de si el que se guardó fue este o el anterior.
      const nombre = elegida?.nombre ?? visita.tiendaTexto;
      setExito({
        mensaje: `Visita registrada${nombre !== '' ? ` en ${nombre}` : ''}.`,
        id: visitaId,
        instrumentos: visita.instrumentos,
      });
      limpiarFormulario();
      await recargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo registrar la visita.');
    } finally {
      setEnviando(false);
    }
  }

  const visitasHoy = visitasSemana.filter((v) => v.fecha === hoy);

  /**
   * Cobertura de la semana, comparando por NOMBRE de tienda y no por id.
   *
   * `VisitaEnLista` no trae el `pdv_id` —trae el nombre ya resuelto—, así que el id no está
   * disponible de este lado. La comparación igual es exacta: los dos lados salen de la misma
   * columna `pdv.name`, el de `misTiendasACargo` y el de la visita. El único caso raro es una
   * tienda escrita a mano cuyo texto coincida con el nombre de una de sus tiendas a cargo, y ahí
   * contarla como cubierta es lo correcto: pasó por ella.
   */
  const cobertura = coberturaDeLaSemana(
    aCargo.map((t) => t.nombre),
    visitasSemana.map((v) => v.tienda),
  );

  return (
    <div className="mx-auto max-w-md space-y-4">
      <header>
        <h1 className="text-xl font-bold text-gray-900">Mi ruta</h1>
        <p className="text-sm text-gray-600">
          Deja constancia de cada parada de tu recorrido. Puedes registrarla en el momento o más
          tarde, cuando puedas sacar el celular.
        </p>
      </header>

      {cobertura !== null && (
        <section className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-sm font-bold text-blue-900">
            Esta semana: {cobertura.cubiertas} de {cobertura.total} tiendas
          </p>
          <div className="mt-2 h-2 w-full rounded bg-blue-100">
            <div className="h-2 rounded bg-blue-600" style={{ width: `${cobertura.pct}%` }} />
          </div>
          <p className="mt-2 text-xs text-blue-900">
            Del lunes a hoy, contando tiendas distintas: pasar dos veces por la misma no cubre a
            las otras.
          </p>
        </section>
      )}

      {exito !== null && (
        <section className="rounded-xl border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-medium text-green-800">
            {exito.mensaje} Puedes registrar la siguiente.
          </p>
          {exito.instrumentos.length > 0 && (
            <ul className="mt-2 space-y-2">
              {exito.instrumentos.map((i) => (
                <li key={i} className="text-sm text-green-900">
                  {/* ACÁ SE ENGANCHAN LOS FORMULARIOS. Cuando existan `/movil/ficha-gondola` y
                      `/movil/ficha-bodega`, esta frase se reemplaza por el enlace comentado abajo:
                      la visita viaja en `?visita=<id>` y es de lo que cuelga el cuestionario.
                      Mientras las pantallas no existan, el enlace mandaría a una ruta rota, así
                      que la pantalla dice la verdad en vez de prometer.

                      <Link
                        to={`${i === 'pg_gondola' ? '/movil/ficha-gondola' : '/movil/ficha-bodega'}?visita=${exito.id}`}
                        className="font-semibold underline"
                      >
                        Llenar {etiquetaInstrumento(i)} de esta visita
                      </Link> */}
                  <span className="font-semibold">{etiquetaInstrumento(i)}:</span> quedó anotado
                  que lo levantaste en esta visita, pero el cuestionario todavía se llena aparte —
                  todavía no está dentro de la app. Tu visita ya quedó guardada igual.
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <Grupo titulo="Registrar visita">
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="fecha-visita">
            ¿De qué día es la visita?
          </label>
          <input
            id="fecha-visita"
            type="date"
            value={fecha}
            max={hoy}
            onChange={(e) => { setFecha(e.target.value); setExito(null); }}
            className="min-h-[44px] w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
          {tardio && (
            /* Avisa, no impide: el usuario pidió expresamente que se pueda subir después. */
            <p className="mt-2 rounded bg-amber-50 p-2 text-sm text-amber-900">
              Estás registrando una visita del {fechaLarga(fecha)}. Va a quedar marcada como
              registro tardío. Está bien: preferimos el dato tarde que no tenerlo.
            </p>
          )}
        </div>

        {/* La visita a la impulsadora NO compite con los formularios: es lo que casi siempre pasa
            en una parada, y los formularios son extras que se marcan además. La primera versión
            hacía elegir una sola cosa entre las tres y el usuario la corrigió: *«el supervisor
            siempre es parte de su visita el ir a visitar a la impulsadora… acá yo puedo poner que
            solo fueron a hacer el levantamiento de la bodega, ok, pero también van a la visita»*. */}
        <div className="mb-4">
          <label
            className={`flex min-h-[44px] items-start gap-2 rounded border p-3 ${
              visitoImpulsadora ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white'
            }`}
          >
            <input
              type="checkbox"
              checked={visitoImpulsadora}
              onChange={(e) => { setVisitoImpulsadora(e.target.checked); setError(''); setExito(null); }}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">
                Visité a la impulsadora
              </span>
              <span className="block text-xs text-gray-600">
                Viene marcado porque es lo normal de una ruta. Desmárcalo solo si pasaste
                únicamente a levantar información.
              </span>
            </span>
          </label>
        </div>

        <fieldset className="mb-4">
          <legend className="mb-1 text-sm font-medium text-gray-700">
            ¿Levantaste algún formulario?
          </legend>
          <p className="mb-2 text-xs text-gray-500">
            Se suman a la visita, no la reemplazan. Puedes marcar los dos.
          </p>
          <div className="space-y-2">
            {INSTRUMENTOS.map((i) => {
              const marcado = instrumentos.includes(i.valor);
              return (
                <label
                  key={i.valor}
                  className={`flex min-h-[44px] items-start gap-2 rounded border p-3 ${
                    marcado ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white'
                  }`}
                >
                  <input
                    type="checkbox"
                    value={i.valor}
                    checked={marcado}
                    onChange={() => alternarInstrumento(i.valor)}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">{i.etiqueta}</span>
                    <span className="block text-xs text-gray-600">{i.ayuda}</span>
                  </span>
                </label>
              );
            })}
          </div>
          {instrumentos.length > 0 && (
            /* Honestidad antes que promesa: el cuestionario largo todavía no vive dentro de la
               app. Sin esta frase, quien marque «góndola» va a buscar las preguntas, no las va a
               encontrar y va a creer que perdió lo que llenó. */
            <p className="mt-2 rounded bg-gray-50 p-2 text-xs text-gray-700">
              Queda anotado que en esta visita levantaste{' '}
              {instrumentos.map((i) => etiquetaInstrumento(i).toLowerCase()).join(' y ')}, pero el
              cuestionario en sí se llena aparte por ahora: todavía no está dentro de la app.
            </p>
          )}
        </fieldset>

        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="tienda">
            ¿En qué {punto} estuviste?
          </label>
          {ayudaLista !== null && <p className="mb-1 text-xs text-gray-500">{ayudaLista}</p>}
          {cargandoTiendas ? (
            <p className="text-sm text-gray-500">Cargando la lista…</p>
          ) : puntos.length === 0 ? (
            <p className="mb-2 rounded bg-gray-50 p-2 text-xs text-gray-700">
              {errorPuntos
                ? 'No se pudo cargar la lista.'
                : 'La lista quedó vacía para lo que marcaste.'}{' '}
              Escribe el nombre y sigue: la visita se registra igual.
            </p>
          ) : (
            <select
              id="tienda"
              value={tiendaSel}
              onChange={(e) => { setTiendaSel(e.target.value); setError(''); setExito(null); }}
              className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">— Elige {formas.el} —</option>
              {puntos.map((t) => (
                <option key={claveDePunto(t)} value={claveDePunto(t)}>
                  {t.detalle !== null ? `${t.nombre} · ${t.detalle}` : t.nombre}
                </option>
              ))}
              <option value={OTRA}>{formas.otro} (escribir el nombre)</option>
            </select>
          )}
          {escribiendoTienda && (
            <input
              type="text"
              aria-label={`Nombre ${formas.del}`}
              value={otraTienda}
              placeholder={`Nombre ${formas.del}, completo`}
              onChange={(e) => { setOtraTienda(e.target.value); setError(''); setExito(null); }}
              className="mt-2 min-h-[44px] w-full rounded border border-gray-300 px-3 py-2 text-base"
            />
          )}
        </div>

        {/* NO hay desplegable de impulsadora, y no es un olvido: hoy no existe forma confiable de
            listarle las suyas —`campaign_staff` está vacía para la campaña viva—, así que el
            desplegable saldría en blanco. Un selector vacío es peor que ninguno: se lee como una
            falla de la app y deja al supervisor sin dónde escribir a quién vio. Las notas sí
            aceptan el nombre, y cuando el reparto exista se convierte en un campo de verdad. */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="notas">
            Notas (opcional)
          </label>
          <p className="mb-1 text-xs text-gray-500">
            A quién viste, cómo la encontraste, lo que haya que contar {formas.del}.
          </p>
          <textarea
            id="notas"
            rows={3}
            value={notas}
            onChange={(e) => { setNotas(e.target.value); setExito(null); }}
            className="w-full rounded border border-gray-300 px-3 py-2 text-base"
          />
        </div>

        {/* La galería se permite A PROPÓSITO, al revés que en el marcado de asistencia: acá la
            foto es un dato de la visita, no la prueba de que alguien estaba en la tienda a esa
            hora, y el registro diferido está aceptado — la foto puede ser de horas antes. */}
        <div className="mb-4">
          <CampoFoto
            etiqueta="Foto de la visita (opcional)"
            nombre="la visita"
            listaTexto="Foto lista para enviar ✓"
            lista={foto !== null}
            disabled={procesandoFoto || enviando}
            onElegir={(e) => void elegirFoto(e)}
          />
          {procesandoFoto && <p className="mt-1 text-xs text-gray-500">Procesando la foto…</p>}
        </div>

        {/* El GPS se cuenta con calma: no condiciona nada, así que no puede leerse como una
            alarma. Sin ubicación la fila queda con `sin_gps`, que lo pone el servidor. */}
        {!gpsListo && (
          <p className="mb-3 rounded bg-gray-50 p-2 text-xs text-gray-600">
            Buscando tu ubicación… no hace falta esperarla para registrar.
          </p>
        )}
        {gpsListo && gps !== null && (
          <p className="mb-3 rounded bg-gray-50 p-2 text-xs text-gray-600">
            Ubicación tomada (precisión ±{Math.round(gps.accuracy)} m).
          </p>
        )}
        {gpsListo && gps === null && (
          <div className="mb-3 rounded bg-gray-50 p-2">
            <p className="text-xs text-gray-700">
              Sin ubicación por ahora. Puedes registrar igual: la visita queda anotada como sin
              GPS y nadie te lo va a reclamar.
            </p>
            {gpsError !== null && (
              <p className="mt-1 text-xs text-gray-500">{gpsErrorMessage(gpsError)}</p>
            )}
            <button
              type="button"
              onClick={() => void pedirUbicacion()}
              className="mt-1 min-h-[44px] text-sm text-blue-700 underline"
            >
              Reintentar ubicación
            </button>
          </div>
        )}

        {error !== '' && (
          <p className="mb-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </p>
        )}

        {ofrecerSinFoto && (
          <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm text-amber-900">
              La visita todavía no se registró. Puedes guardarla sin la foto —el resto del dato no
              se pierde— o volver a intentar con ella.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void enviar(true)}
                disabled={enviando}
                className="min-h-[44px] flex-1 rounded bg-amber-600 px-4 py-2 text-sm font-semibold text-white disabled:bg-gray-300"
              >
                Registrar sin la foto
              </button>
              <button
                type="button"
                onClick={() => void enviar()}
                disabled={enviando}
                className="min-h-[44px] rounded border border-gray-300 px-4 py-2 text-sm"
              >
                Reintentar con la foto
              </button>
            </div>
          </div>
        )}

        {/* Qué falta, DICHO. Un botón gris sin explicación deja a la persona sin saber qué le
            piden, y acá lo único obligatorio es la tienda.

            Se calla mientras está el cartel verde de la visita recién registrada: al limpiarse el
            formulario la tienda queda vacía otra vez, así que sin esta condición el «registrada»
            aparecía con un aviso ámbar pegado debajo y se leía como si algo hubiera fallado. */}
        {falta !== null && !enviando && exito === null && (
          <p className="mb-3 rounded bg-amber-50 p-2 text-sm text-amber-900">{falta}</p>
        )}

        <button
          type="button"
          onClick={() => void enviar()}
          disabled={enviando || procesandoFoto || falta !== null}
          className="w-full rounded-xl bg-blue-600 py-5 text-lg font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
        >
          {enviando ? 'Registrando…' : 'Registrar visita'}
        </button>
      </Grupo>

      <Grupo titulo="Mis visitas de hoy">
        {cargandoVisitas && <p className="text-sm text-gray-500">Cargando…</p>}
        {!cargandoVisitas && errorVisitas !== '' && (
          <p className="text-sm text-gray-600">{errorVisitas}</p>
        )}
        {!cargandoVisitas && errorVisitas === '' && visitasHoy.length === 0 && (
          <p className="text-sm text-gray-500">
            Todavía no registraste visitas hoy. La primera aparece acá apenas la envíes.
          </p>
        )}
        {visitasHoy.length > 0 && (
          <ul className="text-sm">
            {visitasHoy.map((v) => (
              <li key={v.id} className="border-b border-gray-100 py-2 last:border-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-gray-900">{v.tienda}</span>
                  <span className="shrink-0 text-xs text-gray-500">
                    {formatInTimeZone(new Date(v.ocurrioEn ?? v.registradoEn), TZ, 'HH:mm')}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {/* La visita a la impulsadora y los formularios conviven: una parada puede ser
                      las tres cosas, así que cada una es su propia bandera. */}
                  {v.visitoImpulsadora && <Bandera>visita a la impulsadora</Bandera>}
                  {v.instrumentos.map((i) => (
                    <Bandera key={i}>{etiquetaInstrumento(i)}</Bandera>
                  ))}
                  {v.registroTardio && <Bandera>registro tardío</Bandera>}
                  {v.sinGps && <Bandera>sin GPS</Bandera>}
                  {v.tiendaFueraDeMaestra && <Bandera>tienda escrita a mano</Bandera>}
                  {v.fotoPath === null && <Bandera>sin foto</Bandera>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Grupo>

      <Link to="/movil" className="block py-3 text-center text-sm text-blue-700 underline">
        Volver al inicio
      </Link>
    </div>
  );
}
